import React, { useState, useEffect, useMemo } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LogOut, Menu, X, Boxes, BellRing,
  ChevronDown, Briefcase, Search,
  PanelLeftClose, PanelLeftOpen, GripVertical, Command,
} from "lucide-react";
import { subscribeToPush } from "@/lib/push";
import { toast } from "sonner";
import { NAV_GROUPS, filterGroupsByPermissions } from "@/lib/navConfig";
import { api } from "@/lib/api";
import NotificationPopups from "@/components/NotificationPopups";
import CommandPalette from "@/components/CommandPalette";

/**
 * Navegación reorganizada en grupos colapsables por flujo de trabajo:
 *  1. Mi espacio (siempre visible, sin colapsar)
 *  2. Ventas
 *  3. Compras
 *  4. Inventario
 *  5. Facturación SRI
 *  6. Finanzas
 *  7. Maestros
 *  8. Personas
 *  9. Administración
 *
 * Cada item respeta `module`/`submodule` para los permisos. Si todos los items
 * de un grupo se filtran por permisos, el grupo entero se oculta.
 *
 * NOTA: la definición de NAV_GROUPS vive en /app/frontend/src/lib/navConfig.js
 * para ser compartida con el Command Palette (Cmd+K) y evitar duplicación.
 */

const STORAGE_KEY = "pharma_nav_open_groups";
const COLLAPSED_KEY = "pharma_sidebar_collapsed";
const ORDER_KEY = "pharma_nav_group_order";

function loadGroupOrder() {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr;
    }
  } catch (_) { /* ignore */ }
  return null;
}

function loadOpenState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* ignore */ }
  return null;
}

function loadCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch (_) { return false; }
}

function NavGroup({ group, visibleItems, isOpen, onToggle, onItemClick, collapsed, draggable, onDragStart, onDragOver, onDrop, onDragEnd, isDragging, isDragTarget }) {
  const Icon = group.icon || Briefcase;
  if (collapsed) {
    // En modo colapsado: solo mostramos los iconos de cada item, sin agrupar visualmente.
    return (
      <div className="mb-1" data-testid={`nav-group-${group.key}`}>
        <div className="space-y-0.5">
          {visibleItems.map(item => <NavItem key={item.to} item={item} onClick={onItemClick} collapsed />)}
        </div>
      </div>
    );
  }
  if (group.alwaysOpen) {
    return (
      <div className="mb-1" data-testid={`nav-group-${group.key}`}>
        <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-[#6f8d7f] font-semibold flex items-center gap-2">
          <Icon size={11} className="text-[#8ab29e]" />
          {group.label}
        </div>
        <div className="space-y-0.5">
          {visibleItems.map(item => <NavItem key={item.to} item={item} onClick={onItemClick} />)}
        </div>
      </div>
    );
  }
  return (
    <div
      className={`mb-0.5 ${isDragging ? "opacity-40" : ""} ${isDragTarget ? "ring-1 ring-[#205c45] rounded" : ""}`}
      data-testid={`nav-group-${group.key}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <div className={`group flex items-center ${isOpen ? "bg-[#1a3a2d]" : "hover:bg-[#142a20]"}`}>
        {draggable && (
          <span
            className="pl-2 pr-1 py-2 cursor-grab active:cursor-grabbing text-[#5c7368] hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
            title="Arrastrar para reordenar"
            data-testid={`nav-group-drag-${group.key}`}
          >
            <GripVertical size={12} />
          </span>
        )}
        <button
          type="button"
          onClick={onToggle}
          className={`flex-1 flex items-center justify-between ${draggable ? "pl-1 pr-5" : "px-5"} py-2 text-[11px] uppercase tracking-[0.16em] font-semibold transition-colors ${isOpen ? "text-white" : "text-[#8ab29e] hover:text-white"}`}
          data-testid={`nav-group-toggle-${group.key}`}
          aria-expanded={isOpen}
        >
          <span className="flex items-center gap-2">
            <Icon size={14} strokeWidth={1.8} />
            {group.label}
          </span>
          <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`} />
        </button>
      </div>
      {isOpen && (
        <div className="space-y-0.5 mt-0.5 mb-1">
          {visibleItems.map(item => <NavItem key={item.to} item={item} onClick={onItemClick} />)}
        </div>
      )}
    </div>
  );
}

function NavItem({ item, onClick, collapsed }) {
  const Icon = item.icon;
  if (collapsed) {
    return (
      <NavLink
        to={item.to}
        end={item.exact}
        onClick={onClick}
        title={item.label}
        className={({ isActive }) => `sidebar-item-collapsed ${isActive ? "active" : ""}`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon size={18} strokeWidth={1.8} />
      </NavLink>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.exact}
      onClick={onClick}
      className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon size={17} strokeWidth={1.8} />
      <span>{item.label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout, hasModule, hasSubmodule } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const [hovered, setHovered] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => loadOpenState() || { ventas: true, inventario: true });
  const [groupOrder, setGroupOrder] = useState(() => loadGroupOrder() || NAV_GROUPS.map(g => g.key));
  const [dragKey, setDragKey] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);
  // ITER174: cargar logo empresa (si existe) para reemplazar el cuadrado verde
  const [empresaLogo, setEmpresaLogo] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/company/logo")
      .then(r => { if (alive && r?.data?.data_url) setEmpresaLogo(r.data.data_url); })
      .catch(() => { /* sin logo configurado */ });
    return () => { alive = false; };
  }, []);

  // Modo "rail+hover": cuando colapsado y hovering, mostramos contenido expandido en overlay
  const expandedView = !collapsed || hovered;

  // Filtra items por permisos y oculta grupos vacíos; aplica el orden persistido
  const visibleGroups = useMemo(() => {
    const filtered = filterGroupsByPermissions(NAV_GROUPS, { role: user?.role, hasModule, hasSubmodule });
    const groupsByKey = new Map(filtered.map(g => [g.key, g]));
    // Ordenar según groupOrder; los grupos nuevos (no presentes en orden guardado) se agregan al final
    const ordered = [];
    const seen = new Set();
    for (const key of groupOrder) {
      if (groupsByKey.has(key)) { ordered.push(groupsByKey.get(key)); seen.add(key); }
    }
    for (const [key, g] of groupsByKey) {
      if (!seen.has(key)) ordered.push(g);
    }
    return ordered;
  }, [hasModule, hasSubmodule, user?.role, groupOrder]);

  // Auto-expandir el grupo que contiene la ruta activa
  useEffect(() => {
    const path = location.pathname;
    const active = visibleGroups.find(g => !g.alwaysOpen && g.items.some(it => it.to === path || (it.to !== "/" && path.startsWith(it.to))));
    if (active && !openGroups[active.key]) {
      setOpenGroups(prev => ({ ...prev, [active.key]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, visibleGroups]);

  // Persistir estado de colapso
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups)); } catch (_) { /* */ }
  }, [openGroups]);

  // Persistir colapso del sidebar
  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0"); } catch (_) { /* */ }
  }, [collapsed]);

  // Persistir orden de grupos
  useEffect(() => {
    try { localStorage.setItem(ORDER_KEY, JSON.stringify(groupOrder)); } catch (_) { /* */ }
  }, [groupOrder]);

  // ===== Atajo global ⌘K / Ctrl+K para abrir Command Palette =====
  useEffect(() => {
    const onKey = (e) => {
      // Cmd+K (Mac) o Ctrl+K (Win/Linux) para toggle
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ===== Drag & Drop para reordenar grupos =====
  const onGroupDragStart = (key) => (e) => {
    setDragKey(key);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", key); } catch (_) { /* */ }
  };
  const onGroupDragOver = (key) => (e) => {
    if (!dragKey || dragKey === key) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch (_) { /* */ }
    if (dragTarget !== key) setDragTarget(key);
  };
  const onGroupDrop = (key) => (e) => {
    e.preventDefault();
    if (!dragKey || dragKey === key) { setDragKey(null); setDragTarget(null); return; }
    setGroupOrder((prev) => {
      // Trabajar sobre el orden completo de NAV_GROUPS (incluyendo no visibles) para
      // mantener consistencia con grupos ocultos por permisos.
      const base = (prev && prev.length ? prev : NAV_GROUPS.map(g => g.key)).slice();
      // Asegurar que todos los keys de NAV_GROUPS estén
      NAV_GROUPS.forEach(g => { if (!base.includes(g.key)) base.push(g.key); });
      const from = base.indexOf(dragKey);
      const to = base.indexOf(key);
      if (from === -1 || to === -1) return prev;
      const [moved] = base.splice(from, 1);
      base.splice(to, 0, moved);
      return base;
    });
    setDragKey(null);
    setDragTarget(null);
  };
  const onGroupDragEnd = () => { setDragKey(null); setDragTarget(null); };

  const toggleCollapsed = () => setCollapsed((v) => !v);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const toggleGroup = (key) => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

  // ===== Command Palette (⌘K) — reemplaza el buscador inline del sidebar =====
  const [paletteOpen, setPaletteOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Rail reservado cuando el sidebar está plegado, para que el contenido no se desplace al hacer hover */}
      {collapsed && <div className="hidden md:block w-16 shrink-0" aria-hidden="true" />}
      <aside
        onMouseEnter={() => collapsed && setHovered(true)}
        onMouseLeave={() => collapsed && setHovered(false)}
        className={`sidebar-bg ${expandedView ? "w-64" : "w-16"} shrink-0 flex flex-col h-screen transition-all duration-200 ${mobileOpen ? "translate-x-0 fixed inset-y-0 left-0 z-50" : "-translate-x-full fixed inset-y-0 left-0 z-50"} md:translate-x-0 md:flex ${collapsed ? "md:fixed md:left-0 md:top-0 md:z-40" : "md:static"} ${collapsed && hovered ? "shadow-2xl shadow-black/40" : ""}`}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "1" : "0"}
        data-hovered={hovered ? "1" : "0"}
      >
        <div className={`${expandedView ? "p-5" : "p-3 justify-center"} flex items-center gap-3 border-b border-[#0f1d16] relative`}>
          {empresaLogo ? (
            <img
              src={empresaLogo}
              alt="Logo empresa"
              className="w-10 h-10 rounded-md object-contain bg-white p-0.5 shrink-0"
              data-testid="sidebar-logo-empresa"
            />
          ) : (
            <div className="w-10 h-10 rounded-md bg-[#205c45] grid place-items-center shrink-0">
              <Boxes className="text-white" size={22} />
            </div>
          )}
          {expandedView && (
            <div className="min-w-0">
              <div className="font-bold text-white tracking-tight truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>Pharma Stern</div>
              <div className="text-[10px] uppercase tracking-widest text-[#8ab29e]">Ecuador</div>
            </div>
          )}
          {expandedView && !collapsed && (
            <button
              className="hidden md:flex ml-auto text-[#8ab29e] hover:text-white p-1 rounded transition-colors"
              onClick={toggleCollapsed}
              title="Plegar barra lateral"
              data-testid="collapse-sidebar-btn"
            >
              <PanelLeftClose size={16} />
            </button>
          )}
          {expandedView && collapsed && (
            <button
              className="hidden md:flex ml-auto text-[#8ab29e] hover:text-white p-1 rounded transition-colors"
              onClick={() => { setHovered(false); toggleCollapsed(); }}
              title="Fijar barra lateral expandida"
              data-testid="pin-sidebar-btn"
            >
              <PanelLeftOpen size={16} />
            </button>
          )}
          <button className="md:hidden ml-auto text-white" onClick={() => setMobileOpen(false)} data-testid="close-sidebar-btn">
            <X size={20} />
          </button>
        </div>

        {collapsed && !hovered && (
          <button
            className="hidden md:flex items-center justify-center py-2 text-[#8ab29e] hover:text-white hover:bg-[#142a20] transition-colors border-b border-[#0f1d16]"
            onClick={toggleCollapsed}
            title="Expandir barra lateral"
            data-testid="expand-sidebar-btn"
          >
            <PanelLeftOpen size={16} />
          </button>
        )}

        {expandedView && (
          <div className="px-3 pt-3 pb-1" data-testid="sidebar-search">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="w-full h-9 bg-[#0f1d16] text-[#6f8d7f] hover:text-white hover:bg-[#142a20] text-sm rounded-md pl-8 pr-2 flex items-center relative border border-transparent hover:border-[#205c45] transition-colors group"
              data-testid="sidebar-search-input"
              title="Abrir buscador (⌘K / Ctrl+K)"
            >
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6f8d7f] group-hover:text-white pointer-events-none" />
              <span className="flex-1 text-left">Buscar módulo…</span>
              <span className="text-[9px] uppercase tracking-wider text-[#6f8d7f] group-hover:text-white font-semibold px-1.5 py-0.5 border border-[#205c45] rounded flex items-center gap-0.5">
                <Command size={9} /> K
              </span>
            </button>
          </div>
        )}
        <nav className="flex-1 min-h-0 overflow-y-auto py-2">
          {visibleGroups.map((g) => (
            <NavGroup
              key={g.key}
              group={g}
              visibleItems={g.items}
              isOpen={!!openGroups[g.key] || g.alwaysOpen}
              onToggle={() => toggleGroup(g.key)}
              onItemClick={() => setMobileOpen(false)}
              collapsed={!expandedView}
              draggable={!g.alwaysOpen && expandedView}
              onDragStart={onGroupDragStart(g.key)}
              onDragOver={onGroupDragOver(g.key)}
              onDrop={onGroupDrop(g.key)}
              onDragEnd={onGroupDragEnd}
              isDragging={dragKey === g.key}
              isDragTarget={dragTarget === g.key && dragKey !== g.key}
            />
          ))}
        </nav>
        <div className={`${expandedView ? "p-4" : "p-2"} border-t border-[#0f1d16]`}>
          {expandedView && (
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-[#205c45] grid place-items-center text-white font-semibold">
                {(user?.nombres?.[0] || "U") + (user?.apellidos?.[0] || "")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{user?.nombres} {user?.apellidos}</div>
                <div className="text-xs text-[#8ab29e] truncate capitalize">{user?.role}</div>
              </div>
            </div>
          )}
          {!expandedView && (
            <div
              className="w-10 h-10 rounded-full bg-[#205c45] grid place-items-center text-white font-semibold mx-auto mb-2"
              title={`${user?.nombres || ""} ${user?.apellidos || ""}`.trim()}
            >
              {(user?.nombres?.[0] || "U") + (user?.apellidos?.[0] || "")}
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center ${expandedView ? "gap-2 px-3" : "justify-center"} py-2 text-sm rounded-md text-[#c2d6ce] hover:bg-[#1f3a2d] hover:text-white transition-colors`}
            title="Cerrar sesión"
            data-testid="logout-btn"
          >
            <LogOut size={16} /> {expandedView && "Cerrar sesión"}
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-[#d1dbd6] h-14 flex items-center px-4 gap-3 sticky top-0 z-20">
          <button className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="open-sidebar-btn">
            <Menu size={22} />
          </button>
          <div className="font-semibold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Pharma Stern · Panel de gestión
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={() => setPaletteOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-[#d1dbd6] hover:bg-[#e9f2ee] text-[#4a5c53] transition-colors"
              data-testid="header-command-palette-btn"
              title="Buscar módulo (⌘K / Ctrl+K)"
            >
              <Search size={13} />
              <span className="hidden md:inline">Buscar</span>
              <span className="hidden md:flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-[#8ba397] font-semibold px-1 py-0.5 border border-[#d1dbd6] rounded">
                <Command size={9} /> K
              </span>
            </button>
            <button
              onClick={async () => {
                const r = await subscribeToPush();
                if (r.ok) toast.success("Notificaciones activadas");
                else toast.error(r.reason || "No se pudo activar");
              }}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-[#d1dbd6] hover:bg-[#e9f2ee] text-[#4a5c53] transition-colors"
              data-testid="enable-push-btn"
              title="Activar notificaciones push"
            >
              <BellRing size={14} /> Notificaciones
            </button>
            <div className="text-xs text-[#5c7368] hidden sm:block">
              {new Date().toLocaleDateString("es-EC", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-[#f7f9f8] p-4 sm:p-6 lg:p-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      {/* Command Palette global (⌘K) */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* ITER208: alertas emergentes de tareas y eventos programados */}
      <NotificationPopups />
    </div>
  );
}
